import {
  ExternalE2EEKeyProvider,
  isE2EESupported,
  Room,
  RoomEvent,
  Track,
  type Participant,
} from "livekit-client";
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from "react";

import { api, type CallSummary } from "../lib/api";
import { createCallMediaKey, openCallMediaKey } from "../lib/call-e2ee";
import { mediaPermissionDeniedMessage, requestMediaStream, requestNativeMediaPermission } from "../lib/media-permissions";
import { ENCRYPTION_LOST, requireEncryptedCallSupport, requireRoomEncryption, requireScreenCaptureSupport, waitForEncryptionReady } from "../lib/media-security";
import {
  answerCallWhileRequestingPermissions,
  startedCallDisposition,
} from "../lib/call-lifecycle";
import { useRealtimeSubscription } from "./Realtime";
import { useSelf } from "./SelfSession";
import { assertUpdateNotInstalling, registerUpdateBlocker } from "../lib/update-activity";

export type MediaTarget = {
  context: "space" | "dm";
  xid: string;
  label: string;
  callXid?: string;
};

export type WatchService = "youtube" | "netflix" | "disney" | "prime" | "hulu" | "other";

export interface WatchPartyState {
  service: WatchService;
  url: string;
  hostIdentity: string;
  updatedAt: number;
}

interface MediaSessionValue {
  room: Room | null;
  target: MediaTarget | null;
  participants: Participant[];
  connected: boolean;
  connecting: boolean;
  error: string | null;
  microphoneEnabled: boolean;
  deafened: boolean;
  cameraEnabled: boolean;
  screenShareEnabled: boolean;
  watchParty: WatchPartyState | null;
  watchPartyAccepted: boolean;
  incomingCall: CallSummary | null;
  outgoingCall: CallSummary | null;
  activeCall: CallSummary | null;
  missedCalls: CallSummary[];
  callNotice: { id: string; title: string; body: string } | null;
  startCall: (target: MediaTarget, withVideo?: boolean) => Promise<void>;
  acceptCall: (call: CallSummary) => Promise<void>;
  declineCall: (call: CallSummary) => Promise<void>;
  cancelCall: () => Promise<void>;
  rejoinCall: () => Promise<void>;
  dismissMissedCall: (callXid: string) => void;
  dismissCallNotice: () => void;
  leave: () => Promise<void>;
  toggleMicrophone: () => Promise<void>;
  toggleDeafen: () => void;
  toggleCamera: () => Promise<void>;
  toggleScreenShare: () => Promise<void>;
  startWatchParty: (service: WatchService, url: string) => Promise<void>;
  acceptWatchParty: () => void;
  endWatchParty: () => Promise<void>;
}

const MediaSessionContext = createContext<MediaSessionValue | null>(null);
const WATCH_TOPIC = "link.watch-party";
const encoder = new TextEncoder();
const decoder = new TextDecoder();

function isCallSummary(value: unknown): value is CallSummary {
  if (typeof value !== "object" || value === null) return false;
  const call = value as Partial<CallSummary>;
  return typeof call.call_xid === "string" && typeof call.dm_xid === "string" &&
    typeof call.key_context_id === "string" && typeof call.key_envelope_b64 === "string" &&
    typeof call.with_video === "boolean" && typeof call.status === "string" &&
    typeof call.participant_state === "string" && typeof call.initiator === "object" &&
    Array.isArray(call.participants);
}

export function callPeer(call: CallSummary, ownAccountXid: string) {
  if (call.initiator.account_xid !== ownAccountXid) return call.initiator;
  return call.participants.find((participant) => participant.account_xid !== ownAccountXid) ?? call.initiator;
}

function callTarget(call: CallSummary, ownAccountXid: string): MediaTarget {
  const peer = callPeer(call, ownAccountXid);
  return { context: "dm", xid: call.dm_xid, callXid: call.call_xid, label: `${peer.display_name} call` };
}

function callCapabilities() {
  return {
    secureContext: globalThis.isSecureContext === true,
    webCrypto: !!globalThis.crypto?.subtle,
    webRtc: typeof RTCPeerConnection !== "undefined",
    workers: typeof Worker !== "undefined",
    encryptedMedia: isE2EESupported(),
    captureDevices: typeof navigator !== "undefined" && typeof navigator.mediaDevices?.getUserMedia === "function",
    displayCapture: typeof navigator !== "undefined" && typeof navigator.mediaDevices?.getDisplayMedia === "function",
  };
}

async function requestCallPermissions(withVideo: boolean): Promise<void> {
  requireEncryptedCallSupport(callCapabilities());
  if (
    typeof navigator === "undefined" ||
    navigator.mediaDevices?.getUserMedia === undefined
  ) {
    throw new Error(
      "Microphone access is unavailable. Open Link over HTTPS or localhost and allow media permissions in your browser.",
    );
  }
  let stream: MediaStream | null = null;
  try {
    // Native Windows consent precedes capture, including runtimes that reject
    // getUserMedia without raising PermissionRequested. Other platforms retain
    // their normal capture prompt. LiveKit is not joined until this completes.
    stream = await requestMediaStream(navigator.mediaDevices, withVideo);
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "NotAllowedError") {
      throw new Error(
        mediaPermissionDeniedMessage(withVideo, (globalThis as { __LINK_DESKTOP__?: unknown }).__LINK_DESKTOP__),
      );
    }
    if (cause instanceof DOMException && cause.name === "NotFoundError") {
      throw new Error(
        `No usable ${withVideo ? "microphone or camera" : "microphone"} was found on this device.`,
      );
    }
    throw cause;
  } finally {
    stream?.getTracks().forEach((track) => track.stop());
  }
}

export function MediaSessionProvider({ children }: { children: ReactNode }) {
  const self = useSelf();
  const roomRef = useRef<Room | null>(null);
  const e2eeWorkerRef = useRef<Worker | null>(null);
  const targetRef = useRef<MediaTarget | null>(null);
  const activeCallRef = useRef<CallSummary | null>(null);
  const outgoingCallRef = useRef<CallSummary | null>(null);
  const incomingCallRef = useRef<CallSummary | null>(null);
  const startCallOperationRef = useRef<Promise<void> | null>(null);
  const acceptCallOperationRef = useRef<Promise<void> | null>(null);
  const joinMediaOperationRef = useRef<{ callXid: string; promise: Promise<void> } | null>(null);
  const terminalCallOperationRef = useRef<Promise<void> | null>(null);
  const [room, setRoom] = useState<Room | null>(null);
  const [target, setTarget] = useState<MediaTarget | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [watchParty, setWatchParty] = useState<WatchPartyState | null>(null);
  const [watchPartyAccepted, setWatchPartyAccepted] = useState(false);
  const [incomingCall, setIncomingCall] = useState<CallSummary | null>(null);
  const [outgoingCall, setOutgoingCall] = useState<CallSummary | null>(null);
  const [activeCall, setActiveCall] = useState<CallSummary | null>(null);
  const [missedCalls, setMissedCalls] = useState<CallSummary[]>([]);
  const [callNotice, setCallNotice] = useState<{ id: string; title: string; body: string } | null>(null);

  useEffect(() => registerUpdateBlocker(() =>
    roomRef.current !== null || activeCallRef.current !== null
    || outgoingCallRef.current !== null || incomingCallRef.current !== null
    || startCallOperationRef.current !== null || acceptCallOperationRef.current !== null
    || joinMediaOperationRef.current !== null || terminalCallOperationRef.current !== null
  ), []);

  useEffect(() => { activeCallRef.current = activeCall; }, [activeCall]);
  useEffect(() => { outgoingCallRef.current = outgoingCall; }, [outgoingCall]);
  useEffect(() => { incomingCallRef.current = incomingCall; }, [incomingCall]);

  const refresh = useCallback(() => setVersion((value) => value + 1), []);

  const disconnectMedia = useCallback(async () => {
    const active = roomRef.current;
    const e2eeWorker = e2eeWorkerRef.current;
    roomRef.current = null;
    e2eeWorkerRef.current = null;
    targetRef.current = null;
    if (active !== null) {
      for (const publication of active.localParticipant.trackPublications.values()) publication.track?.stop();
      await active.disconnect().catch(() => undefined);
    }
    e2eeWorker?.terminate();
    setRoom(null);
    setTarget(null);
    setConnected(false);
    setConnecting(false);
    setWatchParty(null);
    setWatchPartyAccepted(false);
  }, []);

  const joinMedia = useCallback(async (
    nextTarget: MediaTarget,
    withVideo: boolean,
    call: CallSummary,
  ) => {
    const callXid = nextTarget.callXid ?? call.call_xid;
    const pendingJoin = joinMediaOperationRef.current;
    if (pendingJoin?.callXid === callXid) {
      await pendingJoin.promise;
      return;
    }
    if (pendingJoin !== null) await pendingJoin.promise.catch(() => undefined);
    if (roomRef.current !== null && targetRef.current?.callXid === callXid) return;

    const operation = (async (): Promise<void> => {
      await disconnectMedia();
      if (activeCallRef.current?.call_xid !== call.call_xid) return;
      setConnecting(true);
      setError(null);
      let nextRoom: Room | null = null;
      let e2eeWorker: Worker | null = null;
      let encryptionFailed = false;
      let encryptionEstablished = false;
      try {
        assertUpdateNotInstalling();
        requireEncryptedCallSupport(callCapabilities());
        // Rejoining can skip start/accept's capture check. Always reconcile
        // native consent before LiveKit enables any local media tracks.
        await requestNativeMediaPermission("microphone");
        if (withVideo) await requestNativeMediaPermission("camera");
        if (activeCallRef.current?.call_xid !== call.call_xid) return;
        const rawMediaKey = await openCallMediaKey(call, self.identity.accountXid);
        const keyProvider = new ExternalE2EEKeyProvider();
        try {
          const ownedKeyBuffer = new Uint8Array(rawMediaKey).buffer;
          await keyProvider.setKey(ownedKeyBuffer);
        } finally {
          rawMediaKey.fill(0);
        }
        e2eeWorker = new Worker(new URL("livekit-client/e2ee-worker", import.meta.url), { type: "module" });
        const credentials = await api.media.token({
          context: nextTarget.context,
          target_xid: nextTarget.xid,
          ...(nextTarget.callXid !== undefined ? { call_xid: nextTarget.callXid } : {}),
        });
        nextRoom = new Room({
          adaptiveStream: true,
          dynacast: true,
          encryption: { keyProvider, worker: e2eeWorker },
        });
        await nextRoom.setE2EEEnabled(true);
        if (!nextRoom.localParticipant.isE2EEEnabled) throw new Error(ENCRYPTION_LOST);
        const stopForEncryptionFailure = () => {
          if (encryptionFailed) return;
          encryptionFailed = true;
          // Stop capture immediately, even if the room is still connecting.
          for (const publication of nextRoom?.localParticipant.trackPublications.values() ?? []) publication.track?.stop();
          if (roomRef.current === nextRoom) void disconnectMedia();
          else void nextRoom?.disconnect().catch(() => undefined);
          setError(ENCRYPTION_LOST);
        };
        nextRoom.on(RoomEvent.EncryptionError, stopForEncryptionFailure);
        nextRoom.on(RoomEvent.ParticipantEncryptionStatusChanged, (enabled, participant) => {
          if (encryptionEstablished && !enabled && participant === nextRoom?.localParticipant) stopForEncryptionFailure();
        });
        const onChanged = (): void => refresh();
        nextRoom.on(RoomEvent.ParticipantConnected, onChanged);
        nextRoom.on(RoomEvent.ParticipantDisconnected, onChanged);
        nextRoom.on(RoomEvent.TrackSubscribed, onChanged);
        nextRoom.on(RoomEvent.TrackUnsubscribed, onChanged);
        nextRoom.on(RoomEvent.LocalTrackPublished, onChanged);
        nextRoom.on(RoomEvent.LocalTrackUnpublished, onChanged);
        nextRoom.on(RoomEvent.ActiveSpeakersChanged, onChanged);
        nextRoom.on(RoomEvent.TrackMuted, onChanged);
        nextRoom.on(RoomEvent.TrackUnmuted, onChanged);
        nextRoom.on(RoomEvent.Disconnected, () => { setConnected(false); refresh(); });
        nextRoom.on(RoomEvent.DataReceived, (payload, participant, _kind, topic) => {
          if (topic !== WATCH_TOPIC || participant === undefined) return;
          try {
            const data = JSON.parse(decoder.decode(payload)) as { kind: "start"; party: WatchPartyState } | { kind: "end" };
            if (data.kind === "start") {
              setWatchParty(data.party);
              setWatchPartyAccepted(false);
            } else {
              setWatchParty(null);
              setWatchPartyAccepted(false);
            }
          } catch {
            // Malformed room data never reaches application state.
          }
        });
        await nextRoom.connect(credentials.ws_url, credentials.token);
        if (encryptionFailed) throw new Error(ENCRYPTION_LOST);
        const encryptedRoom = nextRoom;
        await waitForEncryptionReady({
          ready: () => encryptedRoom.isE2EEEnabled && encryptedRoom.localParticipant.isE2EEEnabled,
          subscribe: (check, fail) => {
            encryptedRoom.on(RoomEvent.ParticipantEncryptionStatusChanged, check);
            encryptedRoom.on(RoomEvent.EncryptionError, fail);
            encryptedRoom.on(RoomEvent.Disconnected, fail);
            return () => {
              encryptedRoom.off(RoomEvent.ParticipantEncryptionStatusChanged, check);
              encryptedRoom.off(RoomEvent.EncryptionError, fail);
              encryptedRoom.off(RoomEvent.Disconnected, fail);
            };
          },
        });
        requireRoomEncryption(nextRoom);
        if (encryptionFailed) throw new Error(ENCRYPTION_LOST);
        encryptionEstablished = true;

        // A realtime call-ended event may arrive while LiveKit is connecting.
        // Never install a late room after the call has already closed.
        if (activeCallRef.current?.call_xid !== call.call_xid) {
          await nextRoom.disconnect().catch(() => undefined);
          e2eeWorker.terminate();
          return;
        }

        roomRef.current = nextRoom;
        e2eeWorkerRef.current = e2eeWorker;
        targetRef.current = nextTarget;
        setRoom(nextRoom);
        setTarget(nextTarget);
        setConnected(true);
        await nextRoom.localParticipant.setMicrophoneEnabled(true);
        if (encryptionFailed || roomRef.current !== nextRoom) throw new Error(ENCRYPTION_LOST);
        requireRoomEncryption(nextRoom);
        if (withVideo) await nextRoom.localParticipant.setCameraEnabled(true);
        if (encryptionFailed || roomRef.current !== nextRoom) throw new Error(ENCRYPTION_LOST);
        refresh();
      } catch (cause) {
        if (nextRoom !== null) await nextRoom.disconnect().catch(() => undefined);
        if (roomRef.current === nextRoom) await disconnectMedia();
        e2eeWorker?.terminate();
        if (activeCallRef.current?.call_xid === call.call_xid) {
          setError(cause instanceof Error ? cause.message : "Could not join the call.");
        }
        setConnected(false);
      } finally {
        setConnecting(false);
      }
    })();

    joinMediaOperationRef.current = { callXid, promise: operation };
    try {
      await operation;
    } finally {
      if (joinMediaOperationRef.current?.promise === operation) {
        joinMediaOperationRef.current = null;
      }
    }
  }, [disconnectMedia, refresh, self.identity.accountXid]);

  const startCall = useCallback(async (nextTarget: MediaTarget, withVideo = false) => {
    if (startCallOperationRef.current !== null) {
      await startCallOperationRef.current;
      return;
    }

    const operation = (async (): Promise<void> => {
      assertUpdateNotInstalling();
      const pendingTerminalOperation = terminalCallOperationRef.current;
      if (pendingTerminalOperation !== null) await pendingTerminalOperation;

      const currentActive = activeCallRef.current;
      if (currentActive !== null) {
        if (
          currentActive.dm_xid === nextTarget.xid
          && currentActive.status === "active"
          && currentActive.participant_state === "accepted"
        ) {
          await joinMedia(
            callTarget(currentActive, self.identity.accountXid),
            currentActive.with_video,
            currentActive,
          );
          return;
        }
        throw new Error("You're already in another call. Leave it before starting a new one.");
      }
      if (outgoingCallRef.current !== null || incomingCallRef.current !== null) {
        throw new Error("Finish the current ringing call before starting another one.");
      }

      await requestCallPermissions(withVideo);
      const { dms } = await api.dms.list();
      const dm = dms.find((item) => item.xid === nextTarget.xid);
      if (dm === undefined) throw new Error("This conversation could not be found.");
      const sealedKey = await createCallMediaKey(dm);
      const { call } = await api.calls.start({
        dm_xid: nextTarget.xid,
        with_video: withVideo,
        key_context_id: sealedKey.keyContextId,
        key_envelope_b64: sealedKey.keyEnvelopeB64,
      });

      const disposition = startedCallDisposition(call);
      setIncomingCall(null);
      incomingCallRef.current = null;
      if (disposition === "join") {
        outgoingCallRef.current = null;
        setOutgoingCall(null);
        activeCallRef.current = call;
        setActiveCall(call);
        await joinMedia(callTarget(call, self.identity.accountXid), call.with_video, call);
        return;
      }
      if (disposition === "incoming") {
        outgoingCallRef.current = null;
        setOutgoingCall(null);
        incomingCallRef.current = call;
        setIncomingCall(call);
        return;
      }
      if (disposition === "outgoing") {
        outgoingCallRef.current = call;
        setOutgoingCall(call);
        return;
      }
      throw new Error("The previous call is still ending. Try again in a moment.");
    })();

    startCallOperationRef.current = operation;
    setError(null);
    try {
      await operation;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not start the call.");
    } finally {
      if (startCallOperationRef.current === operation) {
        startCallOperationRef.current = null;
      }
    }
  }, [joinMedia, self.identity.accountXid]);

  const acceptCall = useCallback(async (call: CallSummary) => {
    if (acceptCallOperationRef.current !== null) {
      await acceptCallOperationRef.current;
      return;
    }

    const operation = (async (): Promise<void> => {
      assertUpdateNotInstalling();
      setConnecting(true);
      requireEncryptedCallSupport(callCapabilities());
      const result = await answerCallWhileRequestingPermissions({
        requestPermissions: () => requestCallPermissions(call.with_video),
        acceptOnServer: () => api.calls.accept(call.call_xid),
        onAccepted: ({ call: acceptedCall }) => {
          incomingCallRef.current = null;
          setIncomingCall(null);
          outgoingCallRef.current = null;
          setOutgoingCall(null);
          activeCallRef.current = acceptedCall;
          setActiveCall(acceptedCall);
        },
        rollbackAcceptedCall: async ({ call: acceptedCall }) => {
          if (activeCallRef.current?.call_xid === acceptedCall.call_xid) {
            activeCallRef.current = null;
            setActiveCall(null);
          }
          await disconnectMedia();
          await api.calls.leave(acceptedCall.call_xid);
        },
      });
      await joinMedia(
        callTarget(result.call, self.identity.accountXid),
        result.call.with_video,
        result.call,
      );
    })();

    acceptCallOperationRef.current = operation;
    setError(null);
    try {
      await operation;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not accept the call.");
    } finally {
      if (acceptCallOperationRef.current === operation) {
        acceptCallOperationRef.current = null;
      }
      if (roomRef.current === null) setConnecting(false);
    }
  }, [disconnectMedia, joinMedia, self.identity.accountXid]);

  const declineCall = useCallback(async (call: CallSummary) => {
    if (terminalCallOperationRef.current !== null) {
      await terminalCallOperationRef.current;
      return;
    }
    const operation = (async (): Promise<void> => {
      incomingCallRef.current = null;
      setIncomingCall(null);
      await api.calls.decline(call.call_xid);
    })();
    terminalCallOperationRef.current = operation;
    try {
      await operation;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not decline the call.");
    } finally {
      if (terminalCallOperationRef.current === operation) {
        terminalCallOperationRef.current = null;
      }
    }
  }, []);

  const cancelCall = useCallback(async () => {
    if (terminalCallOperationRef.current !== null) {
      await terminalCallOperationRef.current;
      return;
    }
    const operation = (async (): Promise<void> => {
      const call = outgoingCallRef.current;
      outgoingCallRef.current = null;
      setOutgoingCall(null);
      if (call !== null) await api.calls.cancel(call.call_xid);
    })();
    terminalCallOperationRef.current = operation;
    try {
      await operation;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not cancel the call.");
    } finally {
      if (terminalCallOperationRef.current === operation) {
        terminalCallOperationRef.current = null;
      }
    }
  }, []);

  const leave = useCallback(async () => {
    if (terminalCallOperationRef.current !== null) {
      await terminalCallOperationRef.current;
      return;
    }
    const operation = (async (): Promise<void> => {
      const call = activeCallRef.current;
      activeCallRef.current = null;
      setActiveCall(null);
      await disconnectMedia();
      setError(null);
      if (call !== null) {
        try { await api.calls.leave(call.call_xid); }
        catch { /* Local media is already closed; the server state will expire safely. */ }
      }
    })();
    terminalCallOperationRef.current = operation;
    try {
      await operation;
    } finally {
      if (terminalCallOperationRef.current === operation) {
        terminalCallOperationRef.current = null;
      }
    }
  }, [disconnectMedia]);

  const rejoinCall = useCallback(async () => {
    const call = activeCallRef.current;
    if (call !== null) await joinMedia(callTarget(call, self.identity.accountXid), call.with_video, call);
  }, [joinMedia, self.identity.accountXid]);

  const dismissMissedCall = useCallback((callXid: string) => {
    setMissedCalls((current) => current.filter((call) => call.call_xid !== callXid));
    void api.calls.markRead(callXid).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (self.identity.accountXid.length === 0) return;
    let cancelled = false;
    void api.calls.list().then(({ calls }) => {
      if (cancelled) return;
      const restoredIncoming = calls.find((call) => call.participant_state === "ringing") ?? null;
      incomingCallRef.current = restoredIncoming;
      setIncomingCall(restoredIncoming);
      const restoredOutgoing = calls.find((call) => call.participant_state === "calling") ?? null;
      const restoredActive = calls.find((call) => call.status === "active" && call.participant_state === "accepted") ?? null;
      outgoingCallRef.current = restoredOutgoing;
      activeCallRef.current = restoredActive;
      setOutgoingCall(restoredOutgoing);
      setActiveCall(restoredActive);
      setMissedCalls(calls.filter((call) => call.participant_state === "missed"));
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [self.identity.accountXid]);

  const accountTopics = useMemo(
    () => self.identity.accountXid.length > 0 ? [`account:${self.identity.accountXid}`] : [],
    [self.identity.accountXid],
  );
  const onCallEvent = useCallback((frame: { type: string; payload?: unknown }) => {
    if (!isCallSummary(frame.payload)) return;
    const call = frame.payload;
    if (frame.type === "call.incoming") {
      if (activeCallRef.current === null) {
        incomingCallRef.current = call;
        setIncomingCall(call);
      }
      if (typeof document !== "undefined" && document.hidden && typeof Notification !== "undefined" && Notification.permission === "granted") {
        const peer = callPeer(call, self.identity.accountXid);
        new Notification(`${peer.display_name} is calling`, {
          body: call.with_video ? "Incoming video call" : "Incoming voice call",
          icon: peer.avatar_url ?? undefined,
          tag: `link-call-${call.call_xid}`,
        });
      }
      return;
    }
    if (frame.type === "call.missed") {
      setIncomingCall((current) => {
        const next = current?.call_xid === call.call_xid ? null : current;
        incomingCallRef.current = next;
        return next;
      });
      setMissedCalls((current) => [call, ...current.filter((item) => item.call_xid !== call.call_xid)]);
      return;
    }
    setIncomingCall((current) => {
      if (current?.call_xid !== call.call_xid) return current;
      const next = call.participant_state === "ringing" && ["ringing", "active"].includes(call.status) ? call : null;
      incomingCallRef.current = next;
      return next;
    });
    if (outgoingCallRef.current?.call_xid === call.call_xid) {
      if (call.status === "active" && call.participant_state === "accepted") {
        outgoingCallRef.current = null;
        setOutgoingCall(null);
        activeCallRef.current = call;
        setActiveCall(call);
        void joinMedia(callTarget(call, self.identity.accountXid), call.with_video, call);
      } else if (["ended", "cancelled"].includes(call.status)) {
        outgoingCallRef.current = null;
        setOutgoingCall(null);
        const peer = callPeer(call, self.identity.accountXid);
        const declined = call.participants.some((participant) => participant.state === "declined");
        setCallNotice({
          id: call.call_xid,
          title: declined ? `${peer.display_name} declined the call` : "No answer",
          body: declined ? "The call was not accepted." : `${peer.display_name} did not answer.`,
        });
      } else {
        setOutgoingCall(call);
      }
    }
    if (activeCallRef.current?.call_xid === call.call_xid) {
      if (call.status === "active" && call.participant_state === "accepted") {
        activeCallRef.current = call;
        setActiveCall(call);
      }
      else {
        activeCallRef.current = null;
        setActiveCall(null);
        void disconnectMedia();
        setCallNotice({ id: call.call_xid, title: "Call ended", body: "The voice connection has closed." });
      }
    }
  }, [disconnectMedia, joinMedia, self.identity.accountXid]);
  useRealtimeSubscription(accountTopics, onCallEvent);

  useEffect(() => {
    if (activeCall?.status !== "active" || activeCall.participant_state !== "accepted") return;
    let stopped = false;
    const beat = (): void => {
      void api.calls.heartbeat(activeCall.call_xid).catch(() => {
        if (!stopped) setError("The call connection could not be kept alive.");
      });
    };
    beat();
    const timer = window.setInterval(beat, 20_000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [activeCall?.call_xid, activeCall?.participant_state, activeCall?.status]);

  useEffect(() => () => {
    void roomRef.current?.disconnect();
    e2eeWorkerRef.current?.terminate();
  }, []);

  const toggleMicrophone = useCallback(async () => {
    const active = roomRef.current;
    if (active === null) return;
    const nextEnabled = !active.localParticipant.isMicrophoneEnabled;
    if (nextEnabled) {
      try { requireRoomEncryption(active); }
      catch { await disconnectMedia(); setError(ENCRYPTION_LOST); return; }
    }
    await active.localParticipant.setMicrophoneEnabled(nextEnabled);
    if (self.micMuted === nextEnabled) self.toggleMic();
    refresh();
  }, [disconnectMedia, refresh, self]);

  useEffect(() => {
    const active = roomRef.current;
    if (active === null) return;
    const shouldBeEnabled = !self.micMuted;
    if (active.localParticipant.isMicrophoneEnabled === shouldBeEnabled) return;
    if (shouldBeEnabled) {
      try { requireRoomEncryption(active); }
      catch { void disconnectMedia(); setError(ENCRYPTION_LOST); return; }
    }
    void active.localParticipant.setMicrophoneEnabled(shouldBeEnabled).then(refresh).catch(() => undefined);
  }, [disconnectMedia, refresh, room, self.micMuted]);
  const toggleCamera = useCallback(async () => {
    const active = roomRef.current;
    if (active === null) return;
    setError(null);
    try {
      if (!active.localParticipant.isCameraEnabled) {
        try { requireRoomEncryption(active); }
        catch { await disconnectMedia(); throw new Error(ENCRYPTION_LOST); }
        await requestNativeMediaPermission("camera");
        // A native permission dialog can outlive the call that opened it.
        if (roomRef.current !== active) return;
        requireRoomEncryption(active);
      }
      await active.localParticipant.setCameraEnabled(!active.localParticipant.isCameraEnabled);
      refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not access the camera.");
    }
  }, [disconnectMedia, refresh]);
  const toggleScreenShare = useCallback(async () => {
    const active = roomRef.current;
    if (active === null) return;
    setError(null);
    try {
      const enable = !active.localParticipant.isScreenShareEnabled;
      if (enable) {
        requireScreenCaptureSupport(callCapabilities());
        try { requireRoomEncryption(active); }
        catch { await disconnectMedia(); throw new Error(ENCRYPTION_LOST); }
      }
      // Keep the call directly in the user's click handler: WebKit requires
      // transient activation for its screen picker. Never select a source for them.
      await active.localParticipant.setScreenShareEnabled(enable, { audio: true });
      refresh();
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "NotAllowedError") return;
      setError(cause instanceof Error ? cause.message : "Could not share your screen.");
    }
  }, [disconnectMedia, refresh]);
  const startWatchParty = useCallback(async (service: WatchService, url: string) => {
    const active = roomRef.current;
    if (active === null) throw new Error("Join a call first.");
    const parsed = new URL(url);
    if (parsed.protocol !== "https:") throw new Error("Watch-party links must use HTTPS.");
    const party: WatchPartyState = { service, url: parsed.toString(), hostIdentity: active.localParticipant.identity, updatedAt: Date.now() };
    setWatchParty(party);
    setWatchPartyAccepted(true);
    await active.localParticipant.publishData(encoder.encode(JSON.stringify({ kind: "start", party })), { reliable: true, topic: WATCH_TOPIC });
  }, []);
  const endWatchParty = useCallback(async () => {
    const active = roomRef.current;
    setWatchParty(null);
    setWatchPartyAccepted(false);
    if (active !== null) await active.localParticipant.publishData(encoder.encode(JSON.stringify({ kind: "end" })), { reliable: true, topic: WATCH_TOPIC });
  }, []);

  const participants = useMemo(() => {
    void version;
    if (room === null) return [];
    return [room.localParticipant, ...room.remoteParticipants.values()];
  }, [room, version]);

  const value = useMemo<MediaSessionValue>(() => ({
    room, target, participants, connected, connecting, error,
    microphoneEnabled: room?.localParticipant.isMicrophoneEnabled ?? false,
    deafened: self.deafened,
    cameraEnabled: room?.localParticipant.isCameraEnabled ?? false,
    screenShareEnabled: room?.localParticipant.isScreenShareEnabled ?? false,
    watchParty, watchPartyAccepted, incomingCall, outgoingCall, activeCall, missedCalls,
    callNotice, startCall, acceptCall, declineCall, cancelCall, rejoinCall,
    dismissMissedCall, dismissCallNotice: () => setCallNotice(null), leave,
    toggleMicrophone, toggleDeafen: self.toggleDeafen, toggleCamera, toggleScreenShare, startWatchParty,
    acceptWatchParty: () => setWatchPartyAccepted(true), endWatchParty,
  }), [
    room, target, participants, connected, connecting, error, watchParty, watchPartyAccepted,
    incomingCall, outgoingCall, activeCall, missedCalls, callNotice, startCall, acceptCall, declineCall,
    cancelCall, rejoinCall, dismissMissedCall, leave, toggleMicrophone, self.deafened, self.toggleDeafen, toggleCamera,
    toggleScreenShare, startWatchParty, endWatchParty,
  ]);

  return <MediaSessionContext.Provider value={value}>{children}</MediaSessionContext.Provider>;
}

export function useMediaSession(): MediaSessionValue {
  const value = useContext(MediaSessionContext);
  if (value === null) throw new Error("useMediaSession must be used inside MediaSessionProvider");
  return value;
}

export function participantVideoTrack(participant: Participant) {
  return participant.getTrackPublication(Track.Source.ScreenShare)?.track ?? participant.getTrackPublication(Track.Source.Camera)?.track ?? null;
}

export function participantIsScreenSharing(participant: Participant): boolean {
  return participant.getTrackPublication(Track.Source.ScreenShare)?.track !== undefined;
}
